import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, User, Search, Menu, X, Heart } from 'lucide-react';

/**
 * Template 20: BAZAAR — Marketplace Vibrant
 * Header: White header, magenta logo, bold nav. Mobile: magenta overlay menu.
 */
const Header20 = ({ cartCount = 0 }) => {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [mobileMenuOpen]);

    const navLinks = [
        { label: 'Home', path: '/template20' },
        { label: 'Shop', path: '/template20/shop' },
        { label: 'About', path: '/template20/about' },
        { label: 'Contact', path: '/template20/contact' },
        { label: 'Orders', path: '/template20/orders' },
    ];

    const handleNavClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Rubik:wght@400;500;600;700&family=Nunito:wght@300;400;600&display=swap');
            `}</style>

            <header
                className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
                style={{
                    backgroundColor: scrolled ? 'rgba(255,255,255,0.97)' : '#FFFFFF',
                    borderBottom: scrolled ? '1px solid #F3E8FF' : '1px solid transparent',
                    boxShadow: scrolled ? '0 2px 20px rgba(192,38,211,0.08)' : 'none',
                }}
            >
                <div className="max-w-[1400px] mx-auto px-4 sm:px-6 flex justify-between items-center h-16 sm:h-[72px]">
                    {/* Left: Hamburger (Mobile) + Logo */}
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setMobileMenuOpen(true)}
                            className="lg:hidden w-10 h-10 flex items-center justify-center text-[#1A1A1A] hover:text-[#C026D3] transition-colors"
                        >
                            <Menu className="w-6 h-6" />
                        </button>

                        <h1
                            onClick={() => navigate('/template20')}
                            className="text-xl sm:text-2xl font-bold cursor-pointer tracking-tight"
                            style={{
                                fontFamily: "'Rubik', sans-serif",
                                color: '#C026D3',
                            }}
                        >
                            BAZAAR
                        </h1>
                    </div>

                    {/* Center: Desktop Nav */}
                    <nav className="hidden lg:flex items-center gap-8">
                        {navLinks.map((link) => (
                            <button
                                key={link.label}
                                onClick={() => handleNavClick(link.path)}
                                className="text-sm font-semibold text-[#1A1A1A] hover:text-[#C026D3] transition-colors duration-200 tracking-wide uppercase"
                                style={{ fontFamily: "'Nunito', sans-serif" }}
                            >
                                {link.label}
                            </button>
                        ))}
                    </nav>

                    {/* Right: Icons */}
                    <div className="flex items-center gap-2 sm:gap-3">
                        <button
                            onClick={() => navigate('/template20/shop')}
                            className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center text-[#1A1A1A] hover:text-[#C026D3] hover:bg-[#FDF4FF] rounded-full transition-all duration-200"
                        >
                            <Search className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => navigate('/template20/profile')}
                            className="hidden sm:flex w-10 h-10 items-center justify-center text-[#1A1A1A] hover:text-[#C026D3] hover:bg-[#FDF4FF] rounded-full transition-all duration-200"
                        >
                            <User className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => navigate('/template20/cart')}
                            className="relative w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center text-[#1A1A1A] hover:text-[#C026D3] hover:bg-[#FDF4FF] rounded-full transition-all duration-200"
                        >
                            <ShoppingBag className="w-5 h-5" />
                            {cartCount > 0 && (
                                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#C026D3] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                                    {cartCount}
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Menu Overlay */}
            {mobileMenuOpen && (
                <div className="fixed inset-0 z-[100] lg:hidden">
                    {/* Backdrop */}
                    <div
                        className="absolute inset-0 bg-black/40"
                        onClick={() => setMobileMenuOpen(false)}
                    />

                    {/* Menu Panel */}
                    <div
                        className="absolute top-0 left-0 w-[85%] max-w-[360px] h-full overflow-y-auto"
                        style={{
                            background: 'linear-gradient(135deg, #C026D3 0%, #A21CAF 100%)',
                        }}
                    >
                        {/* Close Button */}
                        <div className="flex items-center justify-between p-5">
                            <h2
                                className="text-2xl font-bold text-white tracking-tight"
                                style={{ fontFamily: "'Rubik', sans-serif" }}
                            >
                                BAZAAR
                            </h2>
                            <button
                                onClick={() => setMobileMenuOpen(false)}
                                className="w-10 h-10 flex items-center justify-center text-white/80 hover:text-white transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* Nav Links */}
                        <nav className="px-5 py-4">
                            {navLinks.map((link, idx) => (
                                <button
                                    key={link.label}
                                    onClick={() => handleNavClick(link.path)}
                                    className="block w-full text-left py-4 text-lg font-semibold text-white/90 hover:text-white border-b border-white/10 transition-colors"
                                    style={{ fontFamily: "'Nunito', sans-serif" }}
                                >
                                    {link.label}
                                </button>
                            ))}
                        </nav>

                        {/* Mobile Menu Footer */}
                        <div className="px-5 py-6 mt-4">
                            <div className="flex gap-3">
                                <button
                                    onClick={() => handleNavClick('/template20/login')}
                                    className="flex-1 py-3 text-sm font-semibold text-[#C026D3] bg-white rounded-full text-center hover:bg-[#FDF4FF] transition-colors"
                                    style={{ fontFamily: "'Nunito', sans-serif" }}
                                >
                                    Login
                                </button>
                                <button
                                    onClick={() => handleNavClick('/template20/signup')}
                                    className="flex-1 py-3 text-sm font-semibold text-white border-2 border-white rounded-full text-center hover:bg-white/10 transition-colors"
                                    style={{ fontFamily: "'Nunito', sans-serif" }}
                                >
                                    Sign Up
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default Header20;
